import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import {
  dateTimeWithLocalOffset,
  getSpaExternalSchedule,
  respondSpaExternalInvitation,
} from "../APIs/SpaAPI";

const readHashParams = () => new URLSearchParams(window.location.hash.replace(/^#/, ""));

const dateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const scheduleRange = () => {
  const from = new Date();
  const to = new Date();
  to.setDate(to.getDate() + 7);
  return {
    from: dateTimeWithLocalOffset(dateKey(from), "00:00"),
    to: dateTimeWithLocalOffset(dateKey(to), "23:59"),
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

function SpaExternalEngagementPage() {
  const hashParams = useMemo(readHashParams, []);
  const token = hashParams.get("token") || "";
  const [tenant, setTenant] = useState(hashParams.get("tenant") || localStorage.getItem("tenantId") || "");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  const canSubmit = Boolean(token && tenant && !loading);

  const loadSchedule = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setMessage("");
    try {
      const data = await getSpaExternalSchedule(scheduleRange(), token, tenant);
      setSchedule(data);
      setStatus("ACCEPTED");
    } catch (error) {
      setMessage(error?.message || "Could not load schedule.");
    } finally {
      setLoading(false);
    }
  };

  const respond = async (decision) => {
    if (!canSubmit) return;
    setLoading(true);
    setMessage("");
    try {
      const invitation = await respondSpaExternalInvitation(token, tenant, decision);
      setStatus(invitation?.status || decision);
      setMessage(decision === "ACCEPT" ? "Engagement accepted." : "Engagement rejected.");
      if (decision === "ACCEPT") {
        const data = await getSpaExternalSchedule(scheduleRange(), token, tenant);
        setSchedule(data);
      } else {
        setSchedule(null);
      }
    } catch (error) {
      setMessage(error?.message || "Could not submit response.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#101010", color: "#fff", py: 6 }}>
      <Container maxWidth="md">
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, bgcolor: "#1F1F1F", borderColor: "rgba(255,255,255,0.14)" }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h4" fontWeight={900}>SPA engagement</Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.72)">
                Accept or reject the external professional invitation.
              </Typography>
            </Box>

            {!token ? <Alert severity="error">Invitation token is missing.</Alert> : null}
            {message ? <Alert severity={message.includes("Could not") ? "error" : "success"}>{message}</Alert> : null}

            <TextField
              fullWidth
              label="Tenant"
              value={tenant}
              onChange={(event) => setTenant(event.target.value)}
              helperText="Required by the SPA external endpoints."
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                disabled={!canSubmit}
                onClick={() => respond("ACCEPT")}
              >
                Accept
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                disabled={!canSubmit}
                onClick={() => respond("REJECT")}
              >
                Reject
              </Button>
              <Button
                variant="outlined"
                startIcon={<EventAvailableIcon />}
                disabled={!canSubmit}
                onClick={loadSchedule}
              >
                View schedule
              </Button>
            </Stack>

            {status ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Status</Typography>
                <Chip size="small" color={status === "ACCEPTED" ? "success" : "default"} label={status} />
              </Stack>
            ) : null}

            {schedule ? (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)" }}>
                <Stack spacing={1}>
                  <Typography fontWeight={900}>Limited schedule</Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.72)">
                    {schedule.operator ? [schedule.operator.name, schedule.operator.surname].filter(Boolean).join(" ") : "-"}
                  </Typography>
                  {(schedule.appointments || []).length ? schedule.appointments.map((appointment) => (
                    <Paper key={appointment.id} variant="outlined" sx={{ p: 1.5, bgcolor: "#141414", borderColor: "rgba(255,255,255,0.12)" }}>
                      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                        <Box>
                          <Typography fontWeight={800}>{appointment.treatment?.name || appointment.treatment || "Treatment"}</Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.72)">
                            {formatDateTime(appointment.startAt)} - {formatDateTime(appointment.endAt)}
                          </Typography>
                        </Box>
                        <Chip size="small" label={appointment.operationalStatus || appointment.commercialStatus || "-"} />
                      </Stack>
                    </Paper>
                  )) : (
                    <Typography variant="body2" color="rgba(255,255,255,0.72)">No appointments in this range.</Typography>
                  )}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default SpaExternalEngagementPage;
